import { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/maplibreWorker";
import { AlertTriangle, Loader2, MapPinned, Redo2, Route as RouteIcon, Search, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { MAP_STYLE_URL, MAP_STYLE_CONFIG_ERROR, MAP_STYLE_LOAD_ERROR_MESSAGE } from "@/lib/mapStyle";
import {
  ROUTE_POINT_TYPES,
  ROUTE_POINT_ICON_KEYS,
  ROUTE_POINT_DEFAULT_ICON,
  getRoutePointIconComponent,
  getRoutePointMeta,
  getRoutePointDefaultApproachMeters,
  getRoutePointDefaultArrivalMeters,
} from "@/lib/routePointIcons";

const MAX_WAYPOINTS = 25;
// Advisory only (never blocks Save) — a route deviating more than this fraction from
// the event_route's own nominal distance gets a gentle warning, per Sprint 09A #34.
const DISTANCE_WARNING_FRACTION = 0.1;
const NL_FALLBACK_CENTER = [5.2913, 52.1326]; // geographic center of the Netherlands — a
// neutral last-resort default, never a hardcoded specific event town.

function formatKm(meters) {
  return `${(meters / 1000).toFixed(2).replace(".", ",")} km`;
}

// Reuses the exact same public Open-Meteo geocoding call already used elsewhere in
// this admin (InfoSettingsManager's "coördinaten ophalen" button) — deliberately not
// a second geocoder.
async function geocodeLocation(query) {
  const res = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?count=1&language=nl&name=${encodeURIComponent(query)}`,
  );
  const geo = await res.json();
  return geo?.results?.[0] || null;
}

// Neither OpenRouteService nor Google Routes can be relied on to have every real
// walking path in their own map data (a small park footpath, or a sidewalk never
// digitized separately from its road) -- no provider setting can route onto
// geometry a provider simply doesn't have. `segmentModes` (one entry per
// consecutive waypoint pair) lets the admin mark an individual segment "straight"
// -- drawn as a direct line, bypassing the routing provider for just that
// segment -- while the rest of the route keeps auto-routing. Falls back to a
// saved route's own persisted modes when they match the current waypoint count,
// otherwise (a fresh route, or the count has since changed) defaults every
// segment to "auto" -- byte-identical to pre-override behavior.
function deriveDefaultSegmentModes(waypoints, saved) {
  if (Array.isArray(saved) && saved.length === Math.max(0, waypoints.length - 1)) return saved;
  return Array(Math.max(0, waypoints.length - 1)).fill("auto");
}

export default function RouteBuilderModal({ route, authAxios, existingNav, eventLocation, onClose, onSaved }) {
  const isRebuildingGpx = existingNav?.source_type === "gpx";
  const initialWaypoints =
    !isRebuildingGpx && Array.isArray(existingNav?.builder_waypoints) ? existingNav.builder_waypoints : [];

  const [waypoints, setWaypoints] = useState(initialWaypoints);
  const [segmentModes, setSegmentModes] = useState(() =>
    deriveDefaultSegmentModes(initialWaypoints, !isRebuildingGpx ? existingNav?.builder_segment_modes : null),
  );
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  // Waypoints could previously only ever be APPENDED (every map click landed at the
  // end of the route) — there was no way to extend a route backward, before its
  // current start. This toggle flips the next click(s) to PREPEND instead, so the
  // admin can keep walking the route earlier in the sequence; the newly-added point
  // becomes the new index 0 (and therefore the new visual "Start" marker) for free,
  // since the existing render logic already derives Start/Finish from array position.
  const [prependMode, setPrependMode] = useState(false);
  // "auto" = every new segment is walk-routed by the configured provider (the
  // original behavior). "manual" = every new segment is drawn as a direct line
  // between the clicked points instead, so the route follows EXACTLY where the
  // admin clicks. This is the guaranteed fallback for an area where the provider's
  // own map data is missing a real path: no provider can route onto geometry it
  // doesn't have, but a manually drawn line always lands exactly where intended.
  // Clicking densely along a curve (every ~10-20m) traces it as accurately as the
  // admin wants.
  const [drawMode, setDrawMode] = useState(
    Array.isArray(existingNav?.builder_segment_modes) &&
      existingNav.builder_segment_modes.length > 0 &&
      existingNav.builder_segment_modes.every((m) => m === "straight")
      ? "manual"
      : "auto",
  );
  const [routeState, setRouteState] = useState(initialWaypoints.length >= 2 ? "calculating" : "idle"); // idle | calculating | ready | error
  const [routeResult, setRouteResult] = useState(null);
  const [routeError, setRouteError] = useState("");
  // Surfaced only on a failed calculation, and only for a superadmin (the endpoint
  // itself is superadmin-gated -- a non-superadmin's 403 here is swallowed silently,
  // never shown as a second error). Answers "which routing provider is actually
  // configured right now" directly at the point of failure, so a Railway env-var
  // mix-up (wrong service, stale deploy, typo'd name) is visible without needing
  // Railway dashboard/log access or a separate API call.
  const [routingDiagnostics, setRoutingDiagnostics] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [mapError, setMapError] = useState(MAP_STYLE_CONFIG_ERROR);

  // Sprint 09B — Route Points / POIs. A completely separate concept from the
  // routing waypoints above: these are participant-facing semantic markers
  // (start/finish/EHBO/water/etc), stored in their own event_route_points table and
  // never mixed into `waypoints`/`builder_waypoints`. "mode" switches the builder
  // between editing routing geometry ("route", the existing behavior, unchanged) and
  // editing these semantic points ("points") — see the mode toggle in the toolbar.
  const [mode, setMode] = useState("route"); // "route" | "points"
  const [points, setPoints] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [pointsSaving, setPointsSaving] = useState(false);
  const [pointImageBusy, setPointImageBusy] = useState(false);
  // The two route-level scavenger-hunt rules. Held locally and written straight
  // through to the route, because they belong to the route itself (event_routes) and
  // not to any one point.
  const [huntRules, setHuntRules] = useState({
    hunt_ordered: !!route?.hunt_ordered,
    hunt_anagram: !!route?.hunt_anagram,
  });
  const [huntRulesSaving, setHuntRulesSaving] = useState(false);
  const [selectedPointId, setSelectedPointId] = useState(null);
  // The point currently open in the editor panel — either a copy of an existing
  // saved point (has `id`) or a brand-new unsaved draft (`id: null`). Editing this
  // object never touches `points` until Save is pressed.
  const [editorPoint, setEditorPoint] = useState(null);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const pointMarkersRef = useRef([]);
  const mapReadyRef = useRef(false);
  const requestIdRef = useRef(0);
  // The MapLibre `click` handler is registered once inside the mount-only map effect
  // below (never re-registered — see the reasons in that effect's comment), so it
  // cannot close over fresh state/closures on every render. This ref always points at
  // the latest click-dispatch closure (route mode -> addWaypoint, points mode ->
  // opens a new point draft); the click handler calls through it, never a captured
  // copy, so it always reflects the current mode/waypoints rather than whatever they
  // were when the map was first created.
  const handleMapClickRef = useRef(null);

  const pushHistory = (prevWaypoints, prevSegmentModes) => {
    setHistory((h) => [...h, { waypoints: prevWaypoints, segmentModes: prevSegmentModes }]);
    setFuture([]);
  };

  const recalculate = async (points, modes) => {
    setDirty(true);
    if (points.length < 2) {
      setRouteState("idle");
      setRouteResult(null);
      return;
    }
    if (points.length > MAX_WAYPOINTS) {
      setRouteState("error");
      setRouteError(`Maximaal ${MAX_WAYPOINTS} routepunten toegestaan`);
      return;
    }
    setRouteState("calculating");
    setRouteError("");
    setRoutingDiagnostics(null);
    const requestId = ++requestIdRef.current;
    try {
      const { data } = await authAxios.post(`/admin/routes/${route.id}/navigation/route`, {
        waypoints: points,
        segment_modes: modes,
        profile: "foot-walking",
      });
      if (requestIdRef.current !== requestId) return; // a newer request superseded this one
      setRouteResult(data);
      setRouteState("ready");
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setRouteState("error");
      setRouteError(err?.response?.data?.detail || "Routeberekening mislukt");
      authAxios
        .get("/admin/diagnostics")
        .then(({ data }) => setRoutingDiagnostics(data))
        .catch(() => {}); // non-superadmin or diagnostics unavailable -- fail silent
    }
  };

  const setWaypointsAndRecalc = (next, nextSegmentModes, { record = true } = {}) => {
    if (record) pushHistory(waypoints, segmentModes);
    setWaypoints(next);
    setSegmentModes(nextSegmentModes);
    setSelectedIndex(null);
    recalculate(next, nextSegmentModes);
  };

  // Every mutation that creates a NEW segment must use the active draw mode.
  // Hardcoding "auto" anywhere silently re-introduces provider routing into a
  // hand-drawn route — which is exactly what made "Eindigen bij startpunt" call
  // the routing provider (and fail) while "Zelf tekenen" was selected.
  const newSegmentMode = drawMode === "manual" ? "straight" : "auto";

  const addWaypoint = (lon, lat) => {
    if (waypoints.length >= MAX_WAYPOINTS) {
      toast.error(`Maximaal ${MAX_WAYPOINTS} routepunten toegestaan`);
      return;
    }
    // Adding the very first waypoint (0 -> 1) creates zero segments -- a segment
    // needs two points. Every subsequent add DOES create exactly one new segment
    // connecting to the existing route. Unconditionally appending here (the
    // original bug) left segmentModes permanently one entry too long from the
    // very first click onward, since the invariant segmentModes.length ===
    // waypoints.length - 1 was already broken by the first point.
    const hasExistingWaypoint = waypoints.length > 0;
    if (prependMode) {
      setWaypointsAndRecalc(
        [[lon, lat], ...waypoints],
        hasExistingWaypoint ? [newSegmentMode, ...segmentModes] : [],
      );
    } else {
      setWaypointsAndRecalc(
        [...waypoints, [lon, lat]],
        hasExistingWaypoint ? [...segmentModes, newSegmentMode] : [],
      );
    }
  };

  const uploadPointImage = async (file) => {
    if (!file || !editorPoint?.id) return;
    setPointImageBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await authAxios.post(
        `/admin/routes/${route.id}/points/${editorPoint.id}/image`,
        body,
      );
      setEditorPoint((prev) => (prev ? { ...prev, image_url: data.image_url } : prev));
      setPoints((prev) => prev.map((p) => (p.id === editorPoint.id ? { ...p, image_url: data.image_url } : p)));
      toast.success("Foto toegevoegd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setPointImageBusy(false);
    }
  };

  const removePointImage = async () => {
    if (!editorPoint?.id) return;
    setPointImageBusy(true);
    try {
      await authAxios.delete(`/admin/routes/${route.id}/points/${editorPoint.id}/image`);
      setEditorPoint((prev) => (prev ? { ...prev, image_url: null } : prev));
      setPoints((prev) => prev.map((p) => (p.id === editorPoint.id ? { ...p, image_url: null } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setPointImageBusy(false);
    }
  };

  const openPointEditorForNewPoint = (lon, lat) => {
    setSelectedPointId(null);
    setEditorPoint({
      id: null,
      point_type: "checkpoint",
      title: "",
      description: "",
      longitude: lon,
      latitude: lat,
      icon_key: ROUTE_POINT_DEFAULT_ICON.checkpoint,
      show_on_map: true,
      notify_on_approach: false,
      approach_distance_meters: null,
      arrival_distance_meters: null,
      hunt_letter: "",
      hunt_question: "",
      hunt_options: [],
      hunt_correct_option: null,
      hunt_challenge: "",
      hunt_answer_text: "",
      hunt_hint: "",
    });
  };

  const openPointEditorForExisting = (point) => {
    setSelectedPointId(point.id);
    setEditorPoint({ ...point });
  };

  // Kept in sync on every render (no deps array) so the click handler registered once
  // in the mount-only map effect always dispatches to the current-render's mode and
  // closures, never a stale one captured at mount.
  useEffect(() => {
    handleMapClickRef.current = (lon, lat) => {
      if (mode === "points") {
        openPointEditorForNewPoint(lon, lat);
      } else {
        addWaypoint(lon, lat);
      }
    };
  });

  const moveWaypoint = (index, lon, lat) => {
    // Dragging a waypoint doesn't change how many segments exist, so segmentModes
    // itself is untouched -- only the coordinate at `index` moves.
    pushHistory(waypoints, segmentModes);
    const next = waypoints.map((wp, i) => (i === index ? [lon, lat] : wp));
    setWaypoints(next);
    recalculate(next, segmentModes);
  };

  const removeSelected = () => {
    if (selectedIndex == null) return;
    const i = selectedIndex;
    const next = waypoints.filter((_, idx) => idx !== i);
    let nextModes;
    if (waypoints.length <= 1) {
      nextModes = [];
    } else if (i === 0) {
      nextModes = segmentModes.slice(1); // only the first segment (0->1) touched the removed start
    } else if (i === waypoints.length - 1) {
      nextModes = segmentModes.slice(0, -1); // only the last segment touched the removed end
    } else {
      // An interior point touches two segments (i-1->i and i->i+1); removing it
      // merges them into one new segment spanning i-1->i+1. There's no single
      // correct answer for which of the two removed segments' modes should "win",
      // so the merged segment follows the active draw mode.
      nextModes = [...segmentModes.slice(0, i - 1), newSegmentMode, ...segmentModes.slice(i + 1)];
    }
    setWaypointsAndRecalc(next, nextModes);
  };

  const closeLoop = () => {
    if (waypoints.length < 2) return;
    const start = waypoints[0];
    const last = waypoints[waypoints.length - 1];
    if (last[0] === start[0] && last[1] === start[1]) return; // already closed
    setWaypointsAndRecalc([...waypoints, [start[0], start[1]]], [...segmentModes, newSegmentMode]);
  };

  // A Start/Finish route point (Sprint 09B) is a separate, participant-facing
  // marker — it never influences routing by itself. Without these two actions the
  // admin has to click a routing waypoint manually on top of a start/finish marker
  // they already placed, which is both duplicate work and impossible to land on
  // exactly. These snap a real waypoint onto that marker's own coordinates.
  const namedPoint = (type) =>
    points.find((p) => p.point_type === type && p.is_active !== false) || null;
  const startPoint = namedPoint("start");
  const finishPoint = namedPoint("finish");

  const beginAtPoint = (point) => {
    if (!point) return;
    if (waypoints.length >= MAX_WAYPOINTS) {
      toast.error(`Maximaal ${MAX_WAYPOINTS} routepunten toegestaan`);
      return;
    }
    const coord = [point.longitude, point.latitude];
    const first = waypoints[0];
    if (first && first[0] === coord[0] && first[1] === coord[1]) return; // already starts there
    setPrependMode(false);
    setWaypointsAndRecalc(
      [coord, ...waypoints],
      waypoints.length > 0 ? [newSegmentMode, ...segmentModes] : [],
    );
  };

  const endAtPoint = (point) => {
    if (!point) return;
    if (waypoints.length >= MAX_WAYPOINTS) {
      toast.error(`Maximaal ${MAX_WAYPOINTS} routepunten toegestaan`);
      return;
    }
    const coord = [point.longitude, point.latitude];
    const last = waypoints[waypoints.length - 1];
    if (last && last[0] === coord[0] && last[1] === coord[1]) return; // already ends there
    setPrependMode(false);
    setWaypointsAndRecalc(
      [...waypoints, coord],
      waypoints.length > 0 ? [...segmentModes, newSegmentMode] : [],
    );
  };

  const clearRoute = () => {
    if (dirty && waypoints.length > 0) {
      if (!window.confirm("Weet je zeker dat je de route wilt wissen?")) return;
    }
    pushHistory(waypoints, segmentModes);
    setWaypoints([]);
    setSegmentModes([]);
    setRouteState("idle");
    setRouteResult(null);
    setRouteError("");
    setSelectedIndex(null);
    setPrependMode(false);
  };

  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFuture((f) => [{ waypoints, segmentModes }, ...f]);
    setWaypoints(prev.waypoints);
    setSegmentModes(prev.segmentModes);
    setSelectedIndex(null);
    recalculate(prev.waypoints, prev.segmentModes);
  };

  const redo = () => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture((f) => f.slice(1));
    setHistory((h) => [...h, { waypoints, segmentModes }]);
    setWaypoints(next.waypoints);
    setSegmentModes(next.segmentModes);
    setSelectedIndex(null);
    recalculate(next.waypoints, next.segmentModes);
  };

  // Switching the drawing mode only decides how the NEXT segments are drawn -- it
  // deliberately leaves every segment already on the map exactly as it is, so an
  // admin can hand-draw the one stretch the provider gets wrong and then switch back
  // to automatic routing to continue. Converting the whole route on a mode switch
  // (the earlier behavior) silently destroyed that hand-drawn work and made the two
  // modes an either/or choice for the entire route.
  const applyDrawMode = (nextMode) => {
    setDrawMode(nextMode);
    setPrependMode(false);
  };

  // The bulk conversion is still available, but only as its own explicit action --
  // never as a side effect of switching modes.
  const segmentsNotMatchingDrawMode = segmentModes.filter((m) => m !== newSegmentMode).length;

  const convertWholeRouteToDrawMode = () => {
    if (segmentsNotMatchingDrawMode === 0) return;
    const label = drawMode === "manual" ? "rechte lijnen" : "automatische routering";
    if (!window.confirm(`Hele route omzetten naar ${label}? Dit overschrijft de huidige instelling van elk segment.`)) return;
    const next = segmentModes.map(() => newSegmentMode);
    pushHistory(waypoints, segmentModes);
    setSegmentModes(next);
    recalculate(waypoints, next);
  };

  const toggleSegmentMode = () => {
    if (selectedIndex == null || selectedIndex >= waypoints.length - 1) return;
    const next = segmentModes.map((m, i) => (i === selectedIndex ? (m === "straight" ? "auto" : "straight") : m));
    pushHistory(waypoints, segmentModes);
    setSegmentModes(next);
    recalculate(waypoints, next);
  };

  const handleClose = () => {
    if (dirty) {
      if (!window.confirm("Je hebt niet-opgeslagen wijzigingen. Route sluiten zonder op te slaan?")) return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (routeState !== "ready" || !routeResult) return;
    if (isRebuildingGpx) {
      if (!window.confirm("Deze route heeft al navigatiegegevens (GPX). Wil je deze vervangen door de route die je op de kaart hebt gemaakt?")) {
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await authAxios.put(`/admin/routes/${route.id}/navigation/builder`, {
        waypoints,
        segment_modes: segmentModes,
        profile: "foot-walking",
      });
      toast.success(`Route opgeslagen — ${formatKm(data.distance_meters)}`);
      setDirty(false);
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  // Route points (POIs) — loaded once on mount, independent of the routing-waypoint
  // save/load lifecycle above (a separate table, a separate API).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get(`/admin/routes/${route.id}/points`);
        if (!cancelled) setPoints(data?.items || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Kon routepunten niet laden");
      } finally {
        if (!cancelled) setPointsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.id]);

  const closePointEditor = () => {
    setEditorPoint(null);
    setSelectedPointId(null);
  };

  const saveHuntRule = async (key, value) => {
    const previous = huntRules[key];
    // Optimistic, then reverted on failure — the same posture the presence grid uses
    // for its per-cell toggles.
    setHuntRules((prev) => ({ ...prev, [key]: value }));
    setHuntRulesSaving(true);
    try {
      await authAxios.patch(`/admin/event-routes/${route.id}`, { [key]: value });
    } catch (err) {
      setHuntRules((prev) => ({ ...prev, [key]: previous }));
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setHuntRulesSaving(false);
    }
  };

  const savePointEditor = async () => {
    if (!editorPoint || !editorPoint.title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    const isHuntPoint = editorPoint.point_type === "hunt";
    // The mode decides which fields mean anything. An empty mode is "vindt en klaar":
    // the point hands over its letter on arrival, with nothing in between.
    const challenge = isHuntPoint ? (editorPoint.hunt_challenge || "") : "";
    const huntQuestion = (editorPoint.hunt_question || "").trim();
    const huntAnswerText = (editorPoint.hunt_answer_text || "").trim();
    // Blank answers are dropped rather than sent: an empty option would be rejected
    // by the backend, and an admin half-way through typing should not be blocked by
    // a row they have not filled in yet.
    const huntOptions =
      challenge === "choice"
        ? (editorPoint.hunt_options || []).map((o) => (o || "").trim()).filter(Boolean)
        : [];
    if (challenge) {
      if (!huntQuestion) {
        toast.error(challenge === "task" ? "Vul de opdracht in" : "Vul de vraag in");
        return;
      }
      if (challenge === "choice") {
        if (huntOptions.length < 2) {
          toast.error("Een meerkeuzevraag heeft minstens twee antwoorden nodig");
          return;
        }
        if (editorPoint.hunt_correct_option == null || editorPoint.hunt_correct_option >= huntOptions.length) {
          toast.error("Kies welk antwoord het juiste is");
          return;
        }
      }
      if (challenge === "open" && !huntAnswerText) {
        toast.error("Vul het verwachte antwoord in");
        return;
      }
    }
    setPointsSaving(true);
    const payload = {
      point_type: editorPoint.point_type,
      title: editorPoint.title.trim(),
      description: editorPoint.description || null,
      longitude: editorPoint.longitude,
      latitude: editorPoint.latitude,
      icon_key: editorPoint.icon_key,
      show_on_map: editorPoint.show_on_map,
      notify_on_approach: editorPoint.notify_on_approach,
      approach_distance_meters: editorPoint.approach_distance_meters,
      arrival_distance_meters: editorPoint.arrival_distance_meters,
      // Sprint 09K. Sent as explicit nulls for every other type: the backend rejects
      // a hunt field with a value on a non-search point, and an existing point being
      // converted away from "Zoekpunt" needs its old letter cleared rather than left
      // behind on an ordinary POI.
      hunt_letter: isHuntPoint ? (editorPoint.hunt_letter || "").trim().toUpperCase() || null : null,
      // Everything below is sent per mode, and as an explicit null outside it — a
      // leftover from a mode the admin has switched away from must be cleared, not
      // left in the row to fail validation later.
      hunt_challenge: challenge || null,
      hunt_question: challenge ? huntQuestion : null,
      hunt_options: challenge === "choice" && huntOptions.length ? huntOptions : null,
      hunt_correct_option:
        challenge === "choice" && huntOptions.length ? editorPoint.hunt_correct_option : null,
      hunt_answer_text: challenge === "open" ? huntAnswerText || null : null,
      hunt_hint: isHuntPoint ? (editorPoint.hunt_hint || "").trim() || null : null,
    };
    try {
      if (editorPoint.id) {
        const { data } = await authAxios.put(`/admin/routes/${route.id}/points/${editorPoint.id}`, payload);
        setPoints((prev) => prev.map((p) => (p.id === editorPoint.id ? data : p)));
        toast.success("Punt bijgewerkt");
      } else {
        const { data } = await authAxios.post(`/admin/routes/${route.id}/points`, payload);
        setPoints((prev) => [...prev, data]);
        toast.success("Punt toegevoegd");
      }
      closePointEditor();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan van punt mislukt");
    } finally {
      setPointsSaving(false);
    }
  };

  const deletePointEditor = async () => {
    if (!editorPoint?.id) {
      closePointEditor();
      return;
    }
    if (!window.confirm("Dit punt verwijderen?")) return;
    setPointsSaving(true);
    try {
      await authAxios.delete(`/admin/routes/${route.id}/points/${editorPoint.id}`);
      setPoints((prev) => prev.filter((p) => p.id !== editorPoint.id));
      closePointEditor();
      toast.success("Punt verwijderd");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setPointsSaving(false);
    }
  };

  const movePointOnDrag = async (point, lon, lat) => {
    try {
      const { data } = await authAxios.put(`/admin/routes/${route.id}/points/${point.id}`, {
        longitude: lon,
        latitude: lat,
      });
      setPoints((prev) => prev.map((p) => (p.id === point.id ? data : p)));
      setEditorPoint((prev) => (prev && prev.id === point.id ? { ...prev, longitude: lon, latitude: lat } : prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verplaatsen mislukt");
    }
  };

  // "Startpunt plaatsen op begin van route" / "Finish plaatsen op einde van route" —
  // convenient defaults from the canonical GeoJSON's first/last coordinate. Admin can
  // still drag the resulting point afterward; this never mutates route geometry
  // itself, only pre-fills a new POI draft.
  const placeStartOrFinishFromRoute = (which) => {
    const geo = routeResult?.geojson || existingNav?.geojson;
    const coords = geo?.geometry?.coordinates;
    if (!coords || coords.length === 0) {
      toast.error("Nog geen routegeometrie beschikbaar om van uit te gaan");
      return;
    }
    const [lon, lat] = which === "start" ? coords[0] : coords[coords.length - 1];
    setMode("points");
    setSelectedPointId(null);
    setEditorPoint({
      id: null,
      point_type: which,
      title: which === "start" ? "Start" : "Finish",
      description: "",
      longitude: lon,
      latitude: lat,
      icon_key: ROUTE_POINT_DEFAULT_ICON[which],
      show_on_map: true,
      notify_on_approach: which === "finish",
      approach_distance_meters: null,
      arrival_distance_meters: null,
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !mapRef.current) return;
    setSearching(true);
    try {
      const place = await geocodeLocation(searchQuery.trim());
      if (!place) {
        toast.error("Locatie niet gevonden");
        return;
      }
      mapRef.current.flyTo({ center: [place.longitude, place.latitude], zoom: 14 });
    } catch {
      toast.error("Kon locatie niet zoeken");
    } finally {
      setSearching(false);
    }
  };

  // Map init — once, on mount. Initial position priority: existing route bbox (Sprint
  // 09A #14) > geocoded event location > a neutral Netherlands center as last resort
  // (never a hardcoded specific town).
  //
  // A ResizeObserver keeps the canvas in sync with its container for the rest of the
  // modal's lifetime (window resize, sidebar/orientation changes) — cheap and correct
  // regardless of the container's size at construction time.
  useEffect(() => {
    if (MAP_STYLE_CONFIG_ERROR) {
      // Kept as a guard: never attempt to load a map style known in advance to be
      // unusable. With the style now fixed in lib/mapStyle.js this cannot currently
      // trigger, but the branch stays so a future configurable style can never
      // reintroduce the unexplained blank map the admin previously saw.
      return undefined;
    }
    let cancelled = false;
    let resizeObserver = null;

    (async () => {
      let center = NL_FALLBACK_CENTER;
      let zoom = 7;
      let bounds = null;
      if (existingNav?.bbox) {
        bounds = [[existingNav.bbox[0], existingNav.bbox[1]], [existingNav.bbox[2], existingNav.bbox[3]]];
      } else if (eventLocation?.trim()) {
        try {
          const place = await geocodeLocation(eventLocation.trim());
          if (place) {
            center = [place.longitude, place.latitude];
            zoom = 13;
          }
        } catch {
          // Fall through to the neutral default — never block the builder on a
          // geocoding failure.
        }
      }

      if (cancelled || !mapContainerRef.current) return;

      const map = new MapLibreMap({
        container: mapContainerRef.current,
        style: MAP_STYLE_URL,
        center,
        zoom,
        ...(bounds ? { bounds, fitBoundsOptions: { padding: 40 } } : {}),
      });
      mapRef.current = map;

      if (process.env.NODE_ENV === "development") {
        const container = mapContainerRef.current;
        // eslint-disable-next-line no-console
        console.debug("[RouteBuilderModal] map created", {
          containerClientWidth: container?.clientWidth,
          containerClientHeight: container?.clientHeight,
        });
        ["style.load", "sourcedata", "idle", "render"].forEach((evt) => {
          map.on(evt, () => console.debug(`[RouteBuilderModal] map event: ${evt}`));
        });
        const canvas = map.getCanvas();
        canvas?.addEventListener("webglcontextlost", () => console.debug("[RouteBuilderModal] webglcontextlost"));
        canvas?.addEventListener("webglcontextrestored", () => console.debug("[RouteBuilderModal] webglcontextrestored"));
      }

      map.getCanvas()?.addEventListener("webglcontextlost", (e) => {
        e.preventDefault();
        setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE);
      });

      map.on("error", () => {
        // MapLibre emits this for a failed style/tile load (tile provider outage,
        // network failure, or a response that isn't valid style JSON) — never log
        // the raw event, it may echo the full request URL.
        console.error("MapLibre style load error");
        setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE);
      });
      map.on("load", () => {
        mapReadyRef.current = true;
        map.addSource("route", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({
          id: "route-line",
          type: "line",
          source: "route",
          paint: { "line-color": "#e8630a", "line-width": 5 },
        });
        // Visual cue for manually-overridden ("straight") segments -- drawn directly
        // from the raw waypoint coordinates (never from routeResult), since a
        // straight segment is by definition just the two waypoints it connects, so
        // this stays correct regardless of what the calculated route looks like.
        map.addSource("route-straight-segments", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({
          id: "route-straight-segments-line",
          type: "line",
          source: "route-straight-segments",
          paint: { "line-color": "#06b6d4", "line-width": 3, "line-dasharray": [2, 2] },
        });
        if (initialWaypoints.length >= 2) {
          recalculate(
            initialWaypoints,
            deriveDefaultSegmentModes(initialWaypoints, !isRebuildingGpx ? existingNav?.builder_segment_modes : null),
          );
        }
      });
      map.on("click", (e) => {
        handleMapClickRef.current?.(e.lngLat.lng, e.lngLat.lat);
      });

      if (mapContainerRef.current && "ResizeObserver" in window) {
        resizeObserver = new ResizeObserver(() => {
          map.resize();
        });
        resizeObserver.observe(mapContainerRef.current);
      }
    })();
    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the route-line source in sync with the latest calculated geometry.
  useEffect(() => {
    if (!mapReadyRef.current || !mapRef.current) return;
    const source = mapRef.current.getSource("route");
    if (!source) return;
    source.setData(
      routeResult?.geojson
        ? { type: "FeatureCollection", features: [routeResult.geojson] }
        : { type: "FeatureCollection", features: [] },
    );
  }, [routeResult]);

  // Keep the dashed "straight segment" overlay in sync with segmentModes/waypoints
  // — independent of routeResult, so it stays accurate even mid-calculation.
  useEffect(() => {
    if (!mapReadyRef.current || !mapRef.current) return;
    const source = mapRef.current.getSource("route-straight-segments");
    if (!source) return;
    const features = segmentModes
      .map((m, i) => (m === "straight" ? [waypoints[i], waypoints[i + 1]] : null))
      .filter(Boolean)
      .map((coords) => ({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } }));
    source.setData({ type: "FeatureCollection", features });
  }, [waypoints, segmentModes]);

  // Keep waypoint markers in sync. Dimmed and non-interactive while in "points" mode
  // (still visible for context — "make the distinction visually obvious" without
  // hiding useful reference geometry).
  useEffect(() => {
    if (!mapRef.current) return;
    const interactive = mode === "route";
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = waypoints.map(([lon, lat], index) => {
      const isStart = index === 0;
      const isFinish = index === waypoints.length - 1 && waypoints.length > 1;
      const el = document.createElement("div");
      el.setAttribute("data-testid", `a4d-builder-waypoint-${index}`);
      el.title = isStart ? "Start" : isFinish ? "Finish" : `Routepunt ${index + 1}`;
      const selected = selectedIndex === index;
      el.style.width = isStart || isFinish ? "18px" : "12px";
      el.style.height = el.style.width;
      el.style.borderRadius = "9999px";
      el.style.border = selected ? "3px solid #06b6d4" : "2px solid white";
      el.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
      el.style.background = isStart ? "#16a34a" : isFinish ? "#dc2626" : "#e8630a";
      el.style.opacity = interactive ? "1" : "0.35";
      el.style.cursor = interactive ? "pointer" : "default";
      const marker = new Marker({ element: el, draggable: interactive })
        .setLngLat([lon, lat])
        .addTo(mapRef.current);
      if (interactive) {
        marker.on("dragend", () => {
          const { lng, lat: newLat } = marker.getLngLat();
          moveWaypoint(index, lng, newLat);
        });
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          setSelectedIndex((cur) => (cur === index ? null : index));
        });
      }
      return marker;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, selectedIndex, mode]);

  // Keep route-point (POI) markers in sync — a plain colored circle with the
  // point's icon rendered into it via renderToStaticMarkup (no React root per
  // marker; MapLibre markers are raw DOM elements, same convention as the waypoint
  // markers above). Dimmed and non-interactive while in "route" mode. The unsaved
  // draft (if any) renders as an extra marker so the admin can see/drag its
  // placement before saving.
  const draftKey = editorPoint && !editorPoint.id
    ? `${editorPoint.point_type}:${editorPoint.icon_key}:${editorPoint.longitude}:${editorPoint.latitude}`
    : null;
  useEffect(() => {
    if (!mapRef.current) return;
    const interactive = mode === "points";
    pointMarkersRef.current.forEach((m) => m.remove());
    const draft = editorPoint && !editorPoint.id ? editorPoint : null;
    const allPoints = draft ? [...points, draft] : points;
    pointMarkersRef.current = allPoints.map((point) => {
      const isDraft = point === draft;
      const meta = getRoutePointMeta(point.point_type);
      const Icon = getRoutePointIconComponent(point.icon_key);
      const isBig = point.point_type === "start" || point.point_type === "finish";
      const selected = isDraft || selectedPointId === point.id;
      const size = isBig ? 30 : 24;
      const el = document.createElement("div");
      el.setAttribute("data-testid", isDraft ? "a4d-route-point-draft" : `a4d-route-point-${point.id}`);
      el.title = point.title || meta.label;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.borderRadius = "9999px";
      el.style.border = selected ? "3px solid #06b6d4" : "2px solid white";
      el.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
      el.style.background = meta.color;
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.style.opacity = interactive ? "1" : point.show_on_map ? "0.4" : "0.2";
      el.style.cursor = interactive ? "pointer" : "default";
      el.innerHTML = renderToStaticMarkup(
        <Icon color="#ffffff" size={Math.round(size * 0.55)} strokeWidth={2.5} />,
      );
      const marker = new Marker({ element: el, draggable: interactive })
        .setLngLat([point.longitude, point.latitude])
        .addTo(mapRef.current);
      if (interactive) {
        marker.on("dragend", () => {
          const { lng, lat } = marker.getLngLat();
          if (isDraft) {
            setEditorPoint((prev) => (prev ? { ...prev, longitude: lng, latitude: lat } : prev));
          } else {
            movePointOnDrag(point, lng, lat);
          }
        });
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          if (!isDraft) openPointEditorForExisting(point);
        });
      }
      return marker;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, mode, selectedPointId, draftKey]);

  const targetKm = route.distance_km != null ? Number(route.distance_km) : null;
  const currentKm = routeResult ? routeResult.distance_meters / 1000 : null;
  const diffKm = targetKm != null && currentKm != null ? currentKm - targetKm : null;
  const showDistanceWarning =
    targetKm != null && currentKm != null && targetKm > 0 && Math.abs(diffKm) / targetKm > DISTANCE_WARNING_FRACTION;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-6" data-testid="a4d-route-builder-modal">
      <div className="bg-svj-card border border-svj-border rounded-2xl w-full max-w-4xl h-full sm:h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-svj-border shrink-0">
          <div>
            <div className="text-sm font-bold text-white">Route maken op kaart</div>
            <div className="text-xs text-slate-400">{route.title}</div>
          </div>
          <button type="button" onClick={handleClose} aria-label="Sluiten" data-testid="a4d-route-builder-close" className="h-9 w-9 rounded-full border border-svj-border text-slate-300 hover:text-white flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 pt-2 border-b border-svj-border shrink-0 flex items-center gap-1" data-testid="a4d-route-builder-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("route")}
            data-testid="a4d-route-builder-mode-route"
            className={`px-3 py-1.5 text-xs font-bold rounded-t-lg inline-flex items-center gap-1.5 border-b-2 -mb-px ${
              mode === "route" ? "border-svj-cyan text-white" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <RouteIcon className="h-3.5 w-3.5" /> Route
          </button>
          <button
            type="button"
            onClick={() => {
              setPrependMode(false);
              setMode("points");
            }}
            data-testid="a4d-route-builder-mode-points"
            className={`px-3 py-1.5 text-xs font-bold rounded-t-lg inline-flex items-center gap-1.5 border-b-2 -mb-px ${
              mode === "points" ? "border-svj-cyan text-white" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <MapPinned className="h-3.5 w-3.5" /> Punten{points.length > 0 ? ` (${points.length})` : ""}
          </button>
        </div>

        {mode === "route" && (
          <>
            <div className="px-4 pt-3 shrink-0" data-testid="a4d-route-builder-draw-mode">
              <div className="inline-flex rounded-lg border border-svj-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => applyDrawMode("auto")}
                  data-testid="a4d-route-builder-draw-mode-auto"
                  aria-pressed={drawMode === "auto"}
                  className={`px-3 py-1.5 text-xs font-bold ${
                    drawMode === "auto" ? "bg-svj-cyan/15 text-svj-cyan" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  Automatisch routeren
                </button>
                <button
                  type="button"
                  onClick={() => applyDrawMode("manual")}
                  data-testid="a4d-route-builder-draw-mode-manual"
                  aria-pressed={drawMode === "manual"}
                  className={`px-3 py-1.5 text-xs font-bold border-l border-svj-border ${
                    drawMode === "manual" ? "bg-svj-cyan/15 text-svj-cyan" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  Zelf tekenen
                </button>
              </div>
              {segmentsNotMatchingDrawMode > 0 && (
                <button
                  type="button"
                  onClick={convertWholeRouteToDrawMode}
                  data-testid="a4d-route-builder-convert-whole-route"
                  className="ml-2 h-8 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold align-middle"
                >
                  Hele route omzetten ({segmentsNotMatchingDrawMode})
                </button>
              )}
            </div>
            <div className="px-4 py-2 border-b border-svj-border shrink-0 flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 flex-1 min-w-[180px]">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Zoek locatie…"
                  data-testid="a4d-route-builder-search"
                  className="flex-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                />
                <button type="button" onClick={handleSearch} disabled={searching} className="h-9 w-9 rounded-lg border border-svj-border text-slate-300 hover:text-white flex items-center justify-center shrink-0">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </button>
              </div>
              <button type="button" onClick={undo} disabled={history.length === 0} data-testid="a4d-route-builder-undo" className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                <Undo2 className="h-3.5 w-3.5" /> Undo
              </button>
              <button type="button" onClick={redo} disabled={future.length === 0} data-testid="a4d-route-builder-redo" className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                <Redo2 className="h-3.5 w-3.5" /> Redo
              </button>
              {selectedIndex != null && (
                <button type="button" onClick={removeSelected} data-testid="a4d-route-builder-remove-point" className="h-9 px-3 rounded-lg border border-red-500/50 text-red-400 hover:text-red-300 text-xs font-semibold">
                  Verwijder punt
                </button>
              )}
              {selectedIndex != null && selectedIndex < waypoints.length - 1 && (
                <button
                  type="button"
                  onClick={toggleSegmentMode}
                  data-testid="a4d-route-builder-toggle-segment-mode"
                  aria-pressed={segmentModes[selectedIndex] === "straight"}
                  title="Gebruik dit als de automatische routering hier het echte pad niet vindt (bijv. een pad dat ontbreekt in de kaartdata van de routeprovider)."
                  className={`h-9 px-3 rounded-lg border text-xs font-semibold ${
                    segmentModes[selectedIndex] === "straight"
                      ? "border-svj-cyan bg-svj-cyan/10 text-svj-cyan"
                      : "border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  {segmentModes[selectedIndex] === "straight" ? "Automatisch routeren" : "Rechte lijn naar volgend punt"}
                </button>
              )}
              {startPoint && (
                <button
                  type="button"
                  onClick={() => beginAtPoint(startPoint)}
                  data-testid="a4d-route-builder-begin-at-start-point"
                  title="Zet een routepunt precies op het startpunt dat je bij Punten hebt geplaatst."
                  className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold"
                >
                  Begin bij startpunt
                </button>
              )}
              {finishPoint && (
                <button
                  type="button"
                  onClick={() => endAtPoint(finishPoint)}
                  data-testid="a4d-route-builder-end-at-finish-point"
                  title="Zet een routepunt precies op het finishpunt dat je bij Punten hebt geplaatst."
                  className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold"
                >
                  Eindig bij finishpunt
                </button>
              )}
              {waypoints.length >= 2 && (
                <button type="button" onClick={closeLoop} data-testid="a4d-route-builder-close-loop" className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold">
                  Eindigen bij startpunt
                </button>
              )}
              {waypoints.length >= 1 && (
                <button
                  type="button"
                  onClick={() => setPrependMode((v) => !v)}
                  data-testid="a4d-route-builder-prepend-toggle"
                  aria-pressed={prependMode}
                  className={`h-9 px-3 rounded-lg border text-xs font-semibold ${
                    prependMode
                      ? "border-svj-cyan bg-svj-cyan/10 text-svj-cyan"
                      : "border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  {prependMode ? "Klik om vóór start toe te voegen…" : "Verleng vóór start"}
                </button>
              )}
              <button type="button" onClick={clearRoute} disabled={waypoints.length === 0} data-testid="a4d-route-builder-clear" className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-red-400 text-xs font-semibold disabled:opacity-40">
                Route wissen
              </button>
            </div>
            <p className="px-4 pt-2 text-[11px] text-slate-500 shrink-0">
              {prependMode
                ? "Klik op de kaart om de route te verlengen vóór het huidige startpunt — dit punt wordt het nieuwe begin."
                : drawMode === "manual"
                ? "Zelf tekenen: nieuwe stukken lopen exact langs de punten die je aanklikt, zonder automatische routering. Klik om de ~10–20 m langs een bocht om die nauwkeurig te volgen. Al getekende stukken blijven zoals ze zijn — je kunt tussendoor terug naar Automatisch routeren."
                : "Klik op de kaart om routepunten toe te voegen. Nieuwe stukken volgen automatisch wandelwegen en paden. Loopt de route ergens niet over het echte pad? Schakel over op \"Zelf tekenen\" voor dat stuk en daarna weer terug — eerder getekende stukken blijven behouden."}
            </p>
          </>
        )}

        {mode === "points" && (
          <>
            <div className="px-4 py-2 border-b border-svj-border shrink-0 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => placeStartOrFinishFromRoute("start")}
                data-testid="a4d-route-builder-place-start"
                className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold"
              >
                Startpunt plaatsen op begin van route
              </button>
              <button
                type="button"
                onClick={() => placeStartOrFinishFromRoute("finish")}
                data-testid="a4d-route-builder-place-finish"
                className="h-9 px-3 rounded-lg border border-svj-border text-slate-300 hover:text-white text-xs font-semibold"
              >
                Finish plaatsen op einde van route
              </button>
            </div>
            {points.some((p) => p.point_type === "hunt") && (
              <div
                className="px-4 py-2 border-b border-svj-border shrink-0 space-y-1.5"
                data-testid="a4d-route-hunt-rules"
              >
                <div className="text-[11px] font-bold text-teal-300">Zoektocht-regels voor deze route</div>
                <label className="flex items-start gap-2 text-[11px] text-slate-300">
                  <input
                    type="checkbox"
                    checked={huntRules.hunt_ordered}
                    disabled={huntRulesSaving}
                    onChange={(e) => saveHuntRule("hunt_ordered", e.target.checked)}
                    data-testid="a4d-route-hunt-ordered"
                    className="mt-0.5"
                  />
                  <span>
                    Op volgorde zoeken
                    <span className="block text-[10px] text-slate-500">
                      Een zoekpunt telt pas als alle eerdere punten van deze route gevonden zijn. Alleen zinvol
                      als de punten in looprichting staan — anders wordt een deelnemer geweigerd bij een punt
                      waar die gewoon voor staat.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-[11px] text-slate-300">
                  <input
                    type="checkbox"
                    checked={huntRules.hunt_anagram}
                    disabled={huntRulesSaving}
                    onChange={(e) => saveHuntRule("hunt_anagram", e.target.checked)}
                    data-testid="a4d-route-hunt-anagram"
                    className="mt-0.5"
                  />
                  <span>
                    Letters door elkaar (woordpuzzel)
                    <span className="block text-[10px] text-slate-500">
                      De deelnemer krijgt de verzamelde letters geschud te zien en moet het woord zelf invullen.
                      De route is dan pas af als dat gelukt is.
                    </span>
                  </span>
                </label>
              </div>
            )}
            <p className="px-4 pt-2 text-[11px] text-slate-500 shrink-0">
              Klik op de kaart om een nieuw punt te plaatsen (EHBO, water, checkpoint, etc). Klik op een bestaand punt om het te bewerken.
            </p>
          </>
        )}

        <div className="flex-1 relative min-h-[240px] mx-4 mt-2 rounded-lg overflow-hidden border border-svj-border">
          {/* MapLibre applies its own `.maplibregl-map { position: relative }` CSS
              class directly to whatever container element it's given, which collides
              with (and, depending on CSS bundle order, silently wins the cascade
              over) a `position: absolute` utility on that same element — collapsing
              it to height 0 and producing a fully blank map despite every network
              request succeeding. Give MapLibre a plain, unstyled inner div instead;
              this outer div keeps the absolute/inset-0 positioning against the
              relative map-area wrapper above. */}
          <div className="absolute inset-0">
            <div ref={mapContainerRef} className="w-full h-full" data-testid="a4d-route-builder-map" />
          </div>
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-svj-bg p-4" data-testid="a4d-route-builder-map-error">
              <div className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 max-w-md">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{mapError}</span>
              </div>
            </div>
          )}

          {mode === "points" && editorPoint && (
            <div
              className="absolute top-2 right-2 bottom-2 w-72 max-w-[85%] bg-svj-card border border-svj-border rounded-xl shadow-xl flex flex-col overflow-hidden z-10"
              data-testid="a4d-route-point-editor"
            >
              <div className="px-3 py-2 border-b border-svj-border flex items-center justify-between shrink-0">
                <span className="text-xs font-bold text-white">{editorPoint.id ? "Punt bewerken" : "Nieuw punt"}</span>
                <button type="button" onClick={closePointEditor} aria-label="Sluiten" className="text-slate-400 hover:text-white">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
                <div>
                  <label className="block text-slate-400 mb-1">Type</label>
                  <select
                    value={editorPoint.point_type}
                    onChange={(e) => {
                      const point_type = e.target.value;
                      setEditorPoint((prev) => ({ ...prev, point_type, icon_key: ROUTE_POINT_DEFAULT_ICON[point_type] }));
                    }}
                    data-testid="a4d-route-point-type"
                    className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                  >
                    {ROUTE_POINT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {getRoutePointMeta(t).label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Icoon</label>
                  <div className="grid grid-cols-7 gap-1">
                    {ROUTE_POINT_ICON_KEYS.map((key) => {
                      const IconOpt = getRoutePointIconComponent(key);
                      const active = editorPoint.icon_key === key;
                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() => setEditorPoint((prev) => ({ ...prev, icon_key: key }))}
                          data-testid={`a4d-route-point-icon-${key}`}
                          className={`h-7 w-7 rounded-lg border flex items-center justify-center ${
                            active ? "border-svj-cyan bg-svj-cyan/10 text-svj-cyan" : "border-svj-border text-slate-400 hover:text-white"
                          }`}
                          aria-label={key}
                        >
                          <IconOpt className="h-3.5 w-3.5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Titel</label>
                  <input
                    value={editorPoint.title}
                    onChange={(e) => setEditorPoint((prev) => ({ ...prev, title: e.target.value }))}
                    data-testid="a4d-route-point-title"
                    className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Beschrijving</label>
                  <textarea
                    value={editorPoint.description || ""}
                    onChange={(e) => setEditorPoint((prev) => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    data-testid="a4d-route-point-description"
                    className="w-full px-2 py-1.5 rounded-lg bg-svj-bg border border-svj-border text-white resize-none"
                  />
                </div>
                {editorPoint.point_type === "hunt" && (
                  <div className="rounded-lg border border-teal-500/40 bg-teal-500/5 p-2.5 space-y-2.5">
                    <div className="text-[11px] font-bold text-teal-300">Zoektocht</div>
                    <div>
                      <label className="block text-slate-400 mb-1">Letter</label>
                      <input
                        value={editorPoint.hunt_letter || ""}
                        maxLength={1}
                        onChange={(e) =>
                          setEditorPoint((prev) => ({ ...prev, hunt_letter: e.target.value.toUpperCase() }))
                        }
                        data-testid="a4d-route-point-hunt-letter"
                        className="w-12 h-8 px-2 text-center rounded-lg bg-svj-bg border border-svj-border text-white uppercase"
                      />
                      <p className="mt-1 text-[10px] text-slate-500">
                        De letters van alle zoekpunten van deze route vormen samen het codewoord, in de volgorde
                        waarin ze hier staan.
                      </p>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Wat vraagt dit punt?</label>
                      <select
                        value={editorPoint.hunt_challenge || ""}
                        onChange={(e) =>
                          setEditorPoint((prev) => ({
                            ...prev,
                            hunt_challenge: e.target.value,
                            // Only the fields belonging to the mode being left are
                            // cleared, so switching back and forth while deciding
                            // does not quietly lose the question itself.
                            hunt_options: e.target.value === "choice" ? prev.hunt_options || [] : [],
                            hunt_correct_option: e.target.value === "choice" ? prev.hunt_correct_option : null,
                            hunt_answer_text: e.target.value === "open" ? prev.hunt_answer_text || "" : "",
                          }))
                        }
                        data-testid="a4d-route-point-hunt-challenge"
                        className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                      >
                        <option value="">Niets — letter meteen bij aankomst</option>
                        <option value="choice">Meerkeuzevraag</option>
                        <option value="open">Open vraag (zelf typen)</option>
                        <option value="task">Opdracht om te doen</option>
                      </select>
                    </div>
                    {editorPoint.hunt_challenge && (
                      <div>
                        <label className="block text-slate-400 mb-1">
                          {editorPoint.hunt_challenge === "task" ? "Opdracht" : "Vraag"}
                        </label>
                        <textarea
                          value={editorPoint.hunt_question || ""}
                          onChange={(e) => setEditorPoint((prev) => ({ ...prev, hunt_question: e.target.value }))}
                          rows={2}
                          data-testid="a4d-route-point-hunt-question"
                          className="w-full px-2 py-1.5 rounded-lg bg-svj-bg border border-svj-border text-white resize-none"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                          {editorPoint.hunt_challenge === "task"
                            ? "De deelnemer krijgt de letter zodra die zelf bevestigt dat de opdracht gedaan is. Bijvoorbeeld: tel de treden van de trap, of maak een foto bij het standbeeld."
                            : "De letter komt pas na een goed antwoord. Fout mag opnieuw, zonder straf."}
                        </p>
                      </div>
                    )}
                    {editorPoint.hunt_challenge === "open" && (
                      <div>
                        <label className="block text-slate-400 mb-1">Verwacht antwoord</label>
                        <input
                          value={editorPoint.hunt_answer_text || ""}
                          onChange={(e) =>
                            setEditorPoint((prev) => ({ ...prev, hunt_answer_text: e.target.value }))
                          }
                          data-testid="a4d-route-point-hunt-answer-text"
                          className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                          Hoofdletters, accenten, spaties en leestekens tellen niet mee — "de Oude Molen!" en
                          "oude molen" zijn allebei goed. Houd het antwoord daarom kort.
                        </p>
                      </div>
                    )}
                    {editorPoint.hunt_challenge === "choice" && (
                      <div className="space-y-1.5">
                        <label className="block text-slate-400">Antwoorden</label>
                        {(editorPoint.hunt_options || []).map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="hunt-correct-option"
                              checked={editorPoint.hunt_correct_option === index}
                              onChange={() => setEditorPoint((prev) => ({ ...prev, hunt_correct_option: index }))}
                              data-testid={`a4d-route-point-hunt-correct-${index}`}
                              aria-label={`Antwoord ${index + 1} is juist`}
                            />
                            <input
                              value={option}
                              onChange={(e) =>
                                setEditorPoint((prev) => ({
                                  ...prev,
                                  hunt_options: (prev.hunt_options || []).map((o, i) =>
                                    i === index ? e.target.value : o,
                                  ),
                                }))
                              }
                              data-testid={`a4d-route-point-hunt-option-${index}`}
                              className="flex-1 h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setEditorPoint((prev) => {
                                  const next = (prev.hunt_options || []).filter((_, i) => i !== index);
                                  // Keep the correct answer pointing at the same
                                  // option after a removal, instead of silently
                                  // sliding onto a different one.
                                  let correct = prev.hunt_correct_option;
                                  if (correct === index) correct = null;
                                  else if (correct != null && correct > index) correct -= 1;
                                  return { ...prev, hunt_options: next, hunt_correct_option: correct };
                                })
                              }
                              aria-label="Antwoord verwijderen"
                              className="text-slate-500 hover:text-red-400"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {(editorPoint.hunt_options || []).length < 6 && (
                          <button
                            type="button"
                            onClick={() =>
                              setEditorPoint((prev) => ({ ...prev, hunt_options: [...(prev.hunt_options || []), ""] }))
                            }
                            data-testid="a4d-route-point-hunt-add-option"
                            className="text-[11px] text-svj-cyan hover:underline"
                          >
                            + Antwoord toevoegen
                          </button>
                        )}
                        <p className="text-[10px] text-slate-500">Vink het bolletje aan bij het juiste antwoord.</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-slate-400 mb-1">Hint (optioneel)</label>
                      <input
                        value={editorPoint.hunt_hint || ""}
                        onChange={(e) => setEditorPoint((prev) => ({ ...prev, hunt_hint: e.target.value }))}
                        data-testid="a4d-route-point-hunt-hint"
                        className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                      />
                      <p className="mt-1 text-[10px] text-slate-500">
                        Een raadseltje dat verschijnt zodra de deelnemer in de buurt komt, nog vóór het punt
                        gevonden is — dat is wat van langslopen echt zoeken maakt. Bijvoorbeeld: "kijk omhoog bij
                        de derde lantaarn". Zet er niet het antwoord op de vraag in.
                      </p>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      Een zoekpunt meldt zichzelf altijd bij nadering — dat is hoe het gevonden wordt.
                    </p>
                  </div>
                )}
                {/* A photo is uploaded against an existing point id, so it only
                    appears once the point has been saved — saying so beats a button
                    that silently does nothing on a new point. */}
                <div>
                  <label className="block text-slate-400 mb-1">Foto (optioneel)</label>
                  {!editorPoint.id ? (
                    <p className="text-[10px] text-slate-500">Sla het punt eerst op, daarna kun je een foto toevoegen.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {editorPoint.image_url && (
                        <img
                          src={editorPoint.image_url}
                          alt=""
                          data-testid="a4d-route-point-image-preview"
                          className="w-full max-h-28 object-cover rounded-lg border border-svj-border"
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer text-[11px] text-svj-cyan hover:underline">
                          {editorPoint.image_url ? "Vervangen" : "Foto kiezen"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            data-testid="a4d-route-point-image-input"
                            onChange={(e) => uploadPointImage(e.target.files?.[0])}
                          />
                        </label>
                        {editorPoint.image_url && (
                          <button
                            type="button"
                            onClick={removePointImage}
                            className="text-[11px] text-slate-500 hover:text-red-400"
                          >
                            Verwijderen
                          </button>
                        )}
                        {pointImageBusy && <span className="text-[10px] text-slate-500">Bezig…</span>}
                      </div>
                      <p className="text-[10px] text-slate-500">
                        JPG, PNG of WEBP, max 8MB. Bij een zoekpunt krijgt de deelnemer de foto pas te zien zodra het
                        punt gevonden is.
                      </p>
                    </div>
                  )}
                </div>
                <label className="flex items-center gap-2 text-slate-300">
                  <input
                    type="checkbox"
                    checked={editorPoint.show_on_map}
                    onChange={(e) => setEditorPoint((prev) => ({ ...prev, show_on_map: e.target.checked }))}
                  />
                  Tonen op kaart
                </label>
                <label className="flex items-center gap-2 text-slate-300">
                  <input
                    type="checkbox"
                    checked={editorPoint.notify_on_approach}
                    data-testid="a4d-route-point-notify"
                    onChange={(e) => setEditorPoint((prev) => ({ ...prev, notify_on_approach: e.target.checked }))}
                  />
                  Melding bij nadering
                </label>
                {editorPoint.notify_on_approach && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-400 mb-1">Nadering (m)</label>
                      <input
                        type="number"
                        value={editorPoint.approach_distance_meters ?? ""}
                        placeholder={String(getRoutePointDefaultApproachMeters(editorPoint.point_type))}
                        onChange={(e) =>
                          setEditorPoint((prev) => ({
                            ...prev,
                            approach_distance_meters: e.target.value === "" ? null : Number(e.target.value),
                          }))
                        }
                        className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1">Aankomst (m)</label>
                      <input
                        type="number"
                        value={editorPoint.arrival_distance_meters ?? ""}
                        placeholder={String(getRoutePointDefaultArrivalMeters(editorPoint.point_type))}
                        onChange={(e) =>
                          setEditorPoint((prev) => ({
                            ...prev,
                            arrival_distance_meters: e.target.value === "" ? null : Number(e.target.value),
                          }))
                        }
                        className="w-full h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3 border-t border-svj-border flex items-center justify-between gap-2 shrink-0">
                {editorPoint.id ? (
                  <button
                    type="button"
                    onClick={deletePointEditor}
                    disabled={pointsSaving}
                    data-testid="a4d-route-point-delete"
                    className="text-red-400 hover:text-red-300 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Verwijderen
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={savePointEditor}
                  disabled={pointsSaving || !editorPoint.title.trim()}
                  data-testid="a4d-route-point-save"
                  className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 disabled:opacity-40"
                >
                  {pointsSaving ? "Opslaan…" : "Opslaan"}
                </button>
              </div>
            </div>
          )}

        </div>

        <div className="px-4 py-3 border-t border-svj-border shrink-0 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-3 text-slate-300">
              <span>
                Route:{" "}
                <span className="font-bold text-white" data-testid="a4d-route-builder-distance">
                  {routeState === "calculating" ? "…" : currentKm != null ? formatKm(currentKm * 1000) : "—"}
                </span>
              </span>
              <span>Waypoints: <span className="font-bold text-white">{waypoints.length}</span></span>
              <span>Punten: <span className="font-bold text-white">{pointsLoading ? "…" : points.length}</span></span>
              {targetKm != null && (
                <span className="text-xs text-slate-400">
                  Doelafstand: {targetKm.toLocaleString("nl-NL")} km
                  {diffKm != null && ` · Verschil: ${diffKm >= 0 ? "+" : ""}${diffKm.toFixed(2).replace(".", ",")} km`}
                </span>
              )}
            </div>
            {routeState === "calculating" && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Berekenen…
              </span>
            )}
            {routeState === "ready" && <span className="text-xs font-semibold text-a4d-green">Route klaar</span>}
          </div>

          {routeState === "error" && routeError && (
            <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="a4d-route-builder-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <span>{routeError}</span>
                {routingDiagnostics && (
                  <div className="mt-1 text-[11px] text-red-300/70" data-testid="a4d-route-builder-error-diagnostics">
                    Routing-provider (Railway): env=
                    <span className="font-mono">{routingDiagnostics.routing_provider_env || "(niet ingesteld)"}</span>
                    {" · "}actief=
                    <span className="font-mono">{routingDiagnostics.routing_provider_active || "geen"}</span>
                    {" · "}API-key aanwezig=
                    <span className="font-mono">{routingDiagnostics.routing_provider_api_key_configured ? "ja" : "nee"}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {showDistanceWarning && (
            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                De gemaakte route wijkt {Math.abs(diffKm).toFixed(1).replace(".", ",")} km af van de ingestelde {targetKm}
                {" "}km.
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={handleClose} className="rounded-lg border border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2">
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={routeState !== "ready" || saving}
              data-testid="a4d-route-builder-save"
              className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Route opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
